import React, { useState, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { UserRound, Users, FileText, MessageSquare, Calendar, Image as ImageIcon, Upload, Send, X } from 'lucide-react';
import { toast } from 'sonner';
import { PageHeader } from '../PageHeader';
import { teamSites } from '../data';
import { useApp, TeamPost } from '../store';
import { downscaleImage } from '../image';
function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
/* Composer for a new team post — supports text + an optional image. */
function TeamComposer({
  teamId
}: {
  teamId: string;
}) {
  const {
    addTeamPost,
    user,
    hasIdentity
  } = useApp();
  const fileRef = useRef<HTMLInputElement>(null);
  const [body, setBody] = useState('');
  const [image, setImage] = useState('');
  const [busy, setBusy] = useState(false);
  const onImage = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please choose an image file.');
      return;
    }
    setBusy(true);
    try {
      setImage(await downscaleImage(file, 1024, 0.8));
    } catch {
      toast.error('Sorry, we couldn’t process that image.');
    } finally {
      setBusy(false);
    }
  };
  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!body.trim() && !image) return;
    addTeamPost({
      teamId,
      body: body.trim(),
      image: image || undefined
    });
    setBody('');
    setImage('');
  };
  return <form onSubmit={submit} className="bg-white rounded-xl border border-navy-100 p-4" data-id="element-761" data-mp="vdyc960mko6k">
      <div className="flex gap-3" data-id="element-762" data-mp="10fnmwkjxqzu4">
        {hasIdentity ? <img src={user.avatar} alt="" className="w-10 h-10 rounded-full object-cover shrink-0" data-id="element-763" data-mp="15dz0n6dcpuhm" /> : <div className="w-10 h-10 rounded-full bg-navy-50 text-navy-300 flex items-center justify-center shrink-0" data-id="element-764" data-mp="app3gfikjdc1">
            <UserRound className="w-5 h-5" data-id="element-765" data-mp="pf3d7oq23wry" />
          </div>}
        <div className="flex-1 min-w-0" data-id="element-766" data-mp="pgehdpnez6jl">
          <textarea value={body} onChange={e => setBody(e.target.value)} placeholder="Share an update with the team…" rows={2} className="w-full resize-none px-3 py-2 rounded-lg bg-navy-50 border border-transparent focus:border-gold-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-gold-400/30 text-sm text-navy-900 placeholder:text-navy-400" data-id="element-767" data-mp="knl94rsqlg2r" />
          {image && <div className="relative mt-2 inline-block" data-id="element-768" data-mp="13ci653fiqmoo">
              <img src={image} alt="Attached" className="max-h-40 rounded-lg border border-navy-100" data-id="element-769" data-mp="3a5loo9w08rp2" />
              <button type="button" onClick={() => setImage('')} className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-navy-600 text-white flex items-center justify-center hover:bg-navy-700" aria-label="Remove image" data-id="element-770" data-mp="1knx92atxdav1">
                <X className="w-3.5 h-3.5" data-id="element-771" data-mp="1fwf4zdwluu5u" />
              </button>
            </div>}
          <div className="flex items-center justify-between mt-2" data-id="element-772" data-mp="11xffk419qb8m">
            <button type="button" onClick={() => fileRef.current?.click()} disabled={busy} className="inline-flex items-center gap-1.5 text-sm font-medium text-navy-500 hover:text-navy-800 px-2 py-1 rounded-lg hover:bg-navy-50 disabled:opacity-50" data-id="element-773" data-mp="11za3zsbr0lhg">
              <ImageIcon className="w-4 h-4" data-id="element-774" data-mp="2md0w9bfrqzbc" />
              {busy ? 'Processing…' : 'Photo'}
            </button>
            <input ref={fileRef} type="file" accept="image/*" onChange={onImage} className="hidden" data-id="element-775" data-mp="2rcnbfqf5qpms" />
            <button type="submit" disabled={!body.trim() && !image || busy} className="inline-flex items-center gap-1.5 text-sm font-bold text-navy-900 bg-gold-500 px-4 py-2 rounded-lg hover:bg-gold-400 transition-colors disabled:opacity-40 disabled:cursor-not-allowed" data-id="element-776" data-mp="marlhll0esrc">
              <Send className="w-4 h-4" data-id="element-777" data-mp="8b9rmrsuwpb4" /> Post
            </button>
          </div>
        </div>
      </div>
    </form>;
}
/* A single team post with inline comments. */
function TeamPostCard({
  post,
  teamId
}: {
  post: TeamPost;
  teamId: string;
}) {
  const {
    addTeamComment,
    user,
    hasIdentity
  } = useApp();
  const [draft, setDraft] = useState('');
  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!draft.trim()) return;
    addTeamComment(teamId, post.id, draft.trim());
    setDraft('');
  };
  return <motion.article initial={{
    opacity: 0,
    y: 8
  }} animate={{
    opacity: 1,
    y: 0
  }} className="bg-white rounded-xl border border-navy-100 overflow-hidden" data-id="element-778" data-mp="3grboj8iiro84">
      <div className="p-4" data-id="element-779" data-mp="219yktkqnge48">
        <div className="flex items-center gap-3 mb-3" data-id="element-780" data-mp="26789gy67slr0">
          <img src={post.avatar} alt={post.author} className="w-10 h-10 rounded-full object-cover" data-id="element-781" data-mp="267qcqerao08f" />
          <div data-id="element-782" data-mp="1dit7pdc3fzxm">
            <p className="text-sm font-bold text-navy-900" data-id="element-783" data-mp="1dj2pweqwd2sr">{post.author}</p>
            <p className="text-xs text-navy-400" data-id="element-784" data-mp="1ih4gdkqnk4e9">
              {post.role} · {post.time}
            </p>
          </div>
        </div>
        {post.body && <p className="text-sm text-navy-800 leading-relaxed whitespace-pre-wrap" data-id="element-785" data-mp="1mhoeormype1z">
            {post.body}
          </p>}
      </div>
      {post.image && <img src={post.image} alt="" className="w-full max-h-96 object-cover" data-id="element-786" data-mp="uqqobu82i5jo" />}
      <div className="px-4 py-3 border-t border-navy-50 space-y-3" data-id="element-787" data-mp="yslnas2j06ua">
        {post.comments.map(c => <div key={c.id} className="flex gap-2.5" data-id="element-788" data-mp="22397f0bk5vir">
            <img src={c.avatar} alt="" className="w-7 h-7 rounded-full object-cover shrink-0" data-id="element-789" data-mp="221y5l61oaxua" />
            <div className="bg-navy-50 rounded-lg px-3 py-2 flex-1" data-id="element-790" data-mp="2bt2js3jiyeo2">
              <p className="text-xs font-semibold text-navy-900" data-id="element-791" data-mp="26v9gbi9lvyrh">
                {c.author}{' '}
                <span className="font-normal text-navy-400" data-id="element-792" data-mp="1j56mawm4siq8">· {c.time}</span>
              </p>
              <p className="text-sm text-navy-700" data-id="element-793" data-mp="1e6lmuzepe32u">{c.body}</p>
            </div>
          </div>)}
        <form onSubmit={submit} className="flex gap-2" data-id="element-794" data-mp="1s5v667y7hdb1">
          <input value={draft} onChange={e => setDraft(e.target.value)} placeholder={hasIdentity ? 'Add a comment…' : 'Introduce yourself to comment…'} className="flex-1 px-3 py-2 rounded-lg bg-navy-50 border border-transparent focus:border-gold-400 focus:bg-white focus:outline-none text-sm text-navy-900" data-id="element-795" data-mp="1s7g0bwglyu8d" />
          <button type="submit" disabled={!draft.trim()} className="px-3 py-2 rounded-lg text-navy-500 hover:bg-navy-50 disabled:opacity-40" aria-label="Send comment" data-id="element-796" data-mp="371wbcdgd16lc">
            <Send className="w-4 h-4" data-id="element-797" data-mp="3244mjsb0i0ld" />
          </button>
        </form>
      </div>
    </motion.article>;
}
export function TeamDetailPage() {
  const {
    id = ''
  } = useParams();
  const team = teamSites.find(t => t.id === id);
  const {
    getTeamPosts,
    getTeamFiles,
    addTeamFile
  } = useApp();
  const uploadRef = useRef<HTMLInputElement>(null);
  if (!team) {
    return <div className="text-center py-20" data-id="element-798" data-mp="3g3m4bnpw4dl8">
        <p className="text-navy-500 mb-4" data-id="element-799" data-mp="3l2p711iee7q1">That team couldn't be found.</p>
        <Link to="/teams" className="text-gold-600 font-semibold" data-id="element-800" data-mp="3l0mb60ukdkde">
          Back to Teams & Sites
        </Link>
      </div>;
  }
  const posts = getTeamPosts(team.id);
  const files = getTeamFiles(team.id);
  const onUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    addTeamFile({
      teamId: team.id,
      name: file.name,
      size: formatBytes(file.size)
    });
    toast.success(`${file.name} shared with ${team.name}.`);
  };
  return <div className="max-w-5xl mx-auto" data-id="element-801" data-mp="1zqevlhqxjin3">
      <PageHeader title={team.name} backTo="/teams" backLabel="Back to Teams" data-id="element-802" data-mp="1zrghkh9hhpjl" />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6" data-id="element-803" data-mp="2wel26a7ewl40">
        {/* Main — about + feed */}
        <div className="lg:col-span-2 space-y-6" data-id="element-804" data-mp="2sesudzpueizp">
          <div className="bg-white rounded-xl border border-navy-100 overflow-hidden" data-id="element-805" data-mp="2ney49ydw5xfp">
            <div className={`h-2 w-full ${team.color}`} data-id="element-806" data-mp="2ig3kat46u2u0" />
            <div className="p-6" data-id="element-807" data-mp="2iflg9abaakbj">
              <h2 className="font-serif font-bold text-xl text-navy-900 mb-2" data-id="element-808" data-mp="2iihhp8mcbr4q">
                About {team.name}
              </h2>
              <p className="text-navy-600 leading-relaxed" data-id="element-809" data-mp="170op9uvjhl1j">
                {team.description}
              </p>
            </div>
          </div>

          <div data-id="element-810" data-mp="wyuemuhha7cs">
            <h2 className="font-serif font-bold text-xl text-navy-900 flex items-center gap-2 mb-3" data-id="element-811" data-mp="wv6ng1zi9x18">
              <MessageSquare className="w-5 h-5 text-gold-500" data-id="element-812" data-mp="16v7a9xwl2va6" /> Team Feed
            </h2>
            <div className="space-y-4" data-id="element-813" data-mp="16tcokhdhmz51">
              <TeamComposer teamId={team.id} data-id="element-814" data-mp="1bm5qyylhhvdy" />
              <AnimatePresence data-id="element-815" data-mp="1bo0e8jk89zl7">
                {posts.length > 0 ? posts.map(p => <TeamPostCard key={p.id} post={p} teamId={team.id} data-id="element-816" data-mp="1fpvctgarxyva" />) : <div className="text-center py-8 bg-navy-50 rounded-xl border border-dashed border-navy-200" data-id="element-817" data-mp="1plyfcn9jfzir">
                    <p className="text-navy-500 text-sm" data-id="element-818" data-mp="1ulbbxzq7okyj">
                      No posts yet — be the first to share an update with{' '}
                      {team.name}.
                    </p>
                  </div>}
              </AnimatePresence>
            </div>
          </div>
        </div>

        {/* Sidebar — members, files, events */}
        <div className="space-y-6" data-id="element-819" data-mp="zzo75vuetlbx">
          <div className="bg-white rounded-xl border border-navy-100 p-6" data-id="element-820" data-mp="14w66uhavuthe">
            <h3 className="font-bold text-navy-900 mb-4 flex items-center gap-2" data-id="element-821" data-mp="18y9ib5xjxy2o">
              <Users className="w-5 h-5 text-navy-400" data-id="element-822" data-mp="190dxwl4tclis" /> Members (
              {team.members})
            </h3>
            <div className="flex flex-wrap gap-2" data-id="element-823" data-mp="1iwgxpkc7082k">
              {Array.from({
              length: Math.min(team.members, 7)
            }).map((_, i) => <div key={i} className="w-10 h-10 rounded-full bg-navy-50 text-navy-300 flex items-center justify-center border-2 border-white shadow-sm" aria-hidden="true" data-id="element-824" data-mp="1ntz9rjlib3es" data-mpi={i}>
                  <UserRound className="w-5 h-5" data-id="element-825" data-mp="21n72xfp8k6yl" />
                </div>)}
              {team.members > 7 && <div className="w-10 h-10 rounded-full bg-navy-50 flex items-center justify-center text-xs font-bold text-navy-500 border-2 border-white shadow-sm" data-id="element-826" data-mp="26n36xexxnye5">
                  +{team.members - 7}
                </div>}
            </div>
            <p className="text-xs text-navy-400 mt-3" data-id="element-827" data-mp="26rsp5xrk2kdn">
              The full member directory loads from this team’s workspace once
              connected.
            </p>
          </div>

          <div className="bg-white rounded-xl border border-navy-100 p-6" data-id="element-828" data-mp="22sjvsu0fgoim">
            <div className="flex items-center justify-between mb-4" data-id="element-829" data-mp="2zfy7kdv4zxab">
              <h3 className="font-bold text-navy-900 flex items-center gap-2" data-id="element-830" data-mp="2uh3kgzq4zmke">
                <FileText className="w-5 h-5 text-navy-400" data-id="element-831" data-mp="2pq4zhmu7e0at" /> Files
              </h3>
              <button onClick={() => uploadRef.current?.click()} className="inline-flex items-center gap-1.5 text-xs font-semibold text-navy-700 bg-navy-50 px-2.5 py-1.5 rounded-lg hover:bg-navy-100 transition-colors" data-id="element-832" data-mp="2lp1jn0c5o49l">
                <Upload className="w-3.5 h-3.5" data-id="element-833" data-mp="3dey0hxpomyca" /> Upload
              </button>
              <input ref={uploadRef} type="file" onChange={onUpload} className="hidden" data-id="element-834" data-mp="1bk055icj1wtc" />
            </div>
            {files.length > 0 ? <ul className="space-y-3" data-id="element-835" data-mp="11sm2x2t6yiwi">
                {files.map(f => <li key={f.id} className="flex items-start gap-3" data-id="element-836" data-mp="16qnsm6l4hggy">
                    <FileText className="w-4 h-4 text-gold-500 mt-0.5 shrink-0" data-id="element-837" data-mp="sro4d01iv3lb" />
                    <div className="min-w-0" data-id="element-838" data-mp="xr0tjrfv8mr2">
                      <p className="text-sm font-medium text-navy-800 truncate" data-id="element-839" data-mp="xsvf97yyoiw7">
                        {f.name}
                      </p>
                      <p className="text-xs text-navy-400" data-id="element-840" data-mp="nt4j71ur5vu0">
                        {f.size} · {f.uploaderName} · {f.time}
                      </p>
                    </div>
                  </li>)}
              </ul> : <div className="text-center py-6 bg-navy-50 rounded-lg border border-dashed border-navy-200" data-id="element-841" data-mp="2i5ueno0rrd3i">
                <p className="text-navy-500 text-sm" data-id="element-842" data-mp="6kbpdyivxv7z">
                  No files yet — upload one to share it with the team.
                </p>
              </div>}
          </div>

          <div className="bg-white rounded-xl border border-navy-100 p-6" data-id="element-843" data-mp="gade8sj2bhny">
            <h3 className="font-bold text-navy-900 mb-4 flex items-center gap-2" data-id="element-844" data-mp="bilkya8iuxu4">
              <Calendar className="w-5 h-5 text-navy-400" data-id="element-845" data-mp="pa06oottdd4v" /> Upcoming Events
            </h3>
            <div className="text-center py-6 bg-navy-50 rounded-lg" data-id="element-846" data-mp="l8ygexhvkbf1">
              <p className="text-navy-500 text-sm" data-id="element-847" data-mp="larcxjyrxf9b">No upcoming events.</p>
            </div>
          </div>
        </div>
      </div>
    </div>;
}